import { Component, OnInit, OnDestroy, DestroyRef, inject, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { AutoComplete } from 'primeng/autocomplete';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Checkbox } from 'primeng/checkbox';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { saveAs } from 'file-saver';
import { OutingService, OutingFilters } from '../../services/outing.service';
import { EmployeeService } from '../../services/employee.service';
import { ProjectService } from '../../services/project.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import {
  Outing,
  OutingStatus,
  OutingReportSummary,
  OUTING_STATUS_LABELS,
  CreateOuting,
  UpdateOuting,
  OutingAttachment,
  OutingAttachmentKind,
  OutingParticipant,
  OutingMarketingEmailPreview
} from '../../models/outing.model';
import { Employee } from '../../models/employee.model';
import { Project } from '../../models/project.model';

interface DropdownOption {
  label: string;
  value: string | number | null;
}

@Component({
  selector: 'app-outings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    ConfirmDialog,
    Select,
    AutoComplete,
    MultiSelect,
    InputText,
    InputNumber,
    DatePicker,
    Tag,
    Tooltip,
    Checkbox
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './outings.component.html',
  styleUrl: './outings.component.scss'
})
export class OutingsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  // The dialog's body-appended dropdown overlays float outside the dialog (so they never
  // clip). We hold references so onDialogHide() can force them closed — otherwise a panel
  // left open when the dialog closes would strand on the page.
  @ViewChildren(MultiSelect) private multiSelects!: QueryList<MultiSelect>;

  // Data
  outings: Outing[] = [];
  filteredOutings: Outing[] = [];
  searchText = '';
  report: OutingReportSummary | null = null;
  employees: Employee[] = [];
  projects: Project[] = [];
  loading = false;

  // Filters
  filterYear: number | null = new Date().getFullYear();
  filterQuarter: number | null = null;
  filterProjectId: string | null = null;
  filterStatus: string | null = null;
  filterServiceGroupId: string | null = null;
  serviceGroupFilterOptions: DropdownOption[] = [{ label: 'All service groups', value: null }];

  yearOptions: DropdownOption[] = [];
  quarterOptions: DropdownOption[] = [
    { label: 'All quarters', value: null },
    { label: 'Q1 (Jan–Mar)', value: 1 },
    { label: 'Q2 (Apr–Jun)', value: 2 },
    { label: 'Q3 (Jul–Sep)', value: 3 },
    { label: 'Q4 (Oct–Dec)', value: 4 }
  ];
  statusOptions: DropdownOption[] = [
    { label: 'All statuses', value: null },
    { label: 'Planning', value: 'Planning' },
    { label: 'Done', value: 'Done' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  projectFilterOptions: DropdownOption[] = [];

  // Add / Edit dialog
  dialogVisible = false;
  editingId: string | null = null;
  saving = false;
  // When editing a Completed outing, only the photo link is editable; everything else is locked.
  lockedEdit = false;
  form: {
    projectId: string | null;
    pmId: string | null;
    eventName: string;
    plannedDate: Date | null;
    status: OutingStatus;
    location: string;
    startTime: string;
    budget: number | null;
    actualSpend: number | null;
    remarks: string;
    photosFolderUrl: string;
    participantIds: string[];
    attendedIds: string[];
  } = this.emptyForm();

  // Edit-dialog status choices. "Done" is NOT here — it's reached via the dedicated
  // completion popup, which collects the required OneDrive photos link.
  formStatusOptions: DropdownOption[] = [
    { label: 'Planning', value: OutingStatus.Planning },
    { label: 'Done', value: OutingStatus.Done },
    { label: 'Cancelled', value: OutingStatus.Cancelled }
  ];

  // Attendance popup. Two modes on the SAME dialog:
  //  'complete'   — Planning outing: record who turned up AND flip the status to Done (emails Marketing).
  //  'attendance' — already-Done outing: correct attendance only; the status and every other field are
  //                 re-sent unchanged, so the backend's "Done and photo link unchanged" path sends no email.
  // RSVP replies are deliberately NOT the answer here: people confirm and skip, or show up without
  // ever clicking the link. Only the PM knows who actually participated.
  completeVisible = false;
  completing = false;
  completeTarget: Outing | null = null;
  completeMode: 'complete' | 'attendance' = 'complete';
  // employeeId -> actually participated. Seeded in openComplete/openAttendance.
  attendanceChecks: { [employeeId: string]: boolean } = {};

  // "Email Marketing the photos" confirmation. The automatic send on the Done transition is
  // once-only, so this is the deliberate re-send path (photos added later, mail never arrived,
  // new recipient in config).
  emailVisible = false;
  emailSending = false;
  emailLoadingPreview = false;
  emailTarget: Outing | null = null;
  emailPreview: OutingMarketingEmailPreview | null = null;

  readonly OutingStatus = OutingStatus;
  readonly OutingAttachmentKind = OutingAttachmentKind;
  readonly statusLabels = OUTING_STATUS_LABELS;

  // Attachments (in the edit dialog): photos gallery only.
  attachments: OutingAttachment[] = [];
  uploadingPhoto = false;

  get photoAttachments(): OutingAttachment[] {
    return this.attachments.filter(a => a.kind === OutingAttachmentKind.Photo);
  }

  constructor(
    private outingService: OutingService,
    private employeeService: EmployeeService,
    private projectService: ProjectService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Main header drives search, Export Excel, the "Plan an outing" primary action, and the
    // funnel toggle for the collapsible filter section (matches the Allocation page).
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search event, project, PM…');
    this.pageHeaderService.setPrimaryButtonLabel('Plan an outing');
    this.pageHeaderService.setPrimaryButtonIcon('pi pi-plus');
    this.pageHeaderService.setCanShowPrimaryAction(true);
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.openAdd();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });

    this.buildYearOptions();
    this.loadReferenceData();
    this.loadOutings();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private emptyForm() {
    return {
      projectId: null as string | null,
      pmId: null as string | null,
      eventName: '',
      plannedDate: null as Date | null,
      // A new outing starts in Planning; it advances to Done later via the completion popup.
      status: OutingStatus.Planning,
      location: '',
      startTime: '',
      budget: null as number | null,
      actualSpend: null as number | null,
      remarks: '',
      photosFolderUrl: '',
      participantIds: [] as string[],
      attendedIds: [] as string[]
    };
  }

  private buildYearOptions(): void {
    const current = new Date().getFullYear();
    this.yearOptions = [{ label: 'All years', value: null }];
    for (let y = current + 1; y >= current - 3; y--) {
      this.yearOptions.push({ label: `${y}`, value: y });
    }
  }

  // Editable, filterable dropdown lists (event name + location); grown by users typing new values.
  optionChoices: string[] = [];
  locationChoices: string[] = [];

  // Suggestion arrays shown in the p-autocomplete panels. Populated on demand by
  // completeOption/completeLocation so the panel only opens on chevron click or an
  // explicit search — typing a single character no longer force-opens the list.
  optionSuggestions: string[] = [];
  locationSuggestions: string[] = [];

  // Filter a choice list by the typed query (case-insensitive "contains").
  // An empty query (chevron click) returns the full list.
  private filterChoices(choices: string[], query: string): string[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      return [...choices];
    }
    return choices.filter(c => c.toLowerCase().includes(q));
  }

  completeOption(event: { query: string }): void {
    this.optionSuggestions = this.filterChoices(this.optionChoices, event.query);
  }

  completeLocation(event: { query: string }): void {
    this.locationSuggestions = this.filterChoices(this.locationChoices, event.query);
  }

  private loadReferenceData(): void {
    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[]))),
      projects: this.projectService.getAllProjects().pipe(catchError(() => of([] as Project[]))),
      options: this.outingService.getOptions(),
      locations: this.outingService.getLocations()
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ employees, projects, options, locations }) => {
        this.employees = (employees || []).filter(e => e.isActive !== false);
        this.projects = (projects || []).filter(p => p.isActive !== false);
        this.projectFilterOptions = [
          { label: 'All projects', value: null },
          ...this.projects.map(p => ({ label: p.name, value: p.id }))
        ];
        this.optionChoices = options || [];
        this.locationChoices = locations || [];
      });
  }

  // Reload the option/location dropdown lists (after a save may have added a new value).
  private refreshLookups(): void {
    forkJoin({
      options: this.outingService.getOptions(),
      locations: this.outingService.getLocations()
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ options, locations }) => {
        this.optionChoices = options || [];
        this.locationChoices = locations || [];
      });
  }

  private currentFilters(): OutingFilters {
    return {
      year: this.filterYear ?? undefined,
      quarter: this.filterQuarter ?? undefined,
      projectId: this.filterProjectId ?? undefined,
      status: this.filterStatus ?? undefined
    };
  }

  loadOutings(): void {
    this.loading = true;
    const filters = this.currentFilters();

    forkJoin({
      outings: this.outingService.getAll(filters).pipe(catchError(() => of([] as Outing[]))),
      report: this.outingService.getReport(filters).pipe(catchError(() => of(null)))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ outings, report }) => {
          this.outings = outings;
          this.report = report;
          this.buildServiceGroupFilterOptions();
          this.applySearch();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  onFilterChange(): void {
    this.loadOutings();
  }

  // Service Group is a client-side filter (on participants), so just re-apply — no reload.
  onServiceGroupFilterChange(): void {
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    // Year defaults to the current year, so it doesn't count as an "active" filter on its own.
    return this.filterQuarter != null || this.filterProjectId != null || this.filterStatus != null
      || this.filterServiceGroupId != null || this.filterYear !== new Date().getFullYear();
  }

  clearFilters(): void {
    this.filterYear = new Date().getFullYear();
    this.filterQuarter = null;
    this.filterProjectId = null;
    this.filterStatus = null;
    this.filterServiceGroupId = null;
    this.loadOutings();
  }

  // Service groups present among the loaded outings' participants (for the filter dropdown).
  private buildServiceGroupFilterOptions(): void {
    const seen = new Map<string, string>();
    for (const o of this.outings) {
      for (const p of o.participants) {
        if (p.serviceGroupId && p.serviceGroupName && !seen.has(p.serviceGroupId)) {
          seen.set(p.serviceGroupId, p.serviceGroupName);
        }
      }
    }
    this.serviceGroupFilterOptions = [
      { label: 'All service groups', value: null },
      ...Array.from(seen.entries())
        .map(([value, label]) => ({ label, value }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }

  // Client-side search + service-group filter across the loaded (server-filtered) rows.
  private applySearch(): void {
    const term = this.searchText.trim().toLowerCase();
    this.filteredOutings = this.outings.filter(o => {
      const matchesTerm = !term
        || [o.eventName, o.projectName, o.projectCode, o.pmName]
          .some(v => (v ?? '').toLowerCase().includes(term));
      const matchesSg = !this.filterServiceGroupId
        || o.participants.some(p => p.serviceGroupId === this.filterServiceGroupId);
      return matchesTerm && matchesSg;
    });
    // Grey out Export in the header when there's nothing to export.
    this.pageHeaderService.setExportDisabled(this.filteredOutings.length === 0);
  }

  // ---------- employee options for the participant multiselect ----------
  get employeeOptions(): DropdownOption[] {
    return this.employees.map(e => ({ label: e.name, value: e.id }));
  }

  get pmOptions(): DropdownOption[] {
    return this.employees.map(e => ({ label: e.name, value: e.id }));
  }

  // Comma-separated names of the currently selected participants (tooltip + hint line).
  selectedParticipantNames(): string {
    if (!this.form.participantIds.length) {
      return '';
    }
    const byId = new Map(this.employees.map(e => [e.id, e.name]));
    return this.form.participantIds
      .map(id => byId.get(id) ?? '')
      .filter(Boolean)
      .join(', ');
  }

  // Only participants can be marked as attended.
  get attendedOptions(): DropdownOption[] {
    return this.employees
      .filter(e => this.form.participantIds.includes(e.id))
      .map(e => ({ label: e.name, value: e.id }));
  }

  // ---------- dialog ----------
  openAdd(): void {
    this.editingId = null;
    this.lockedEdit = false;
    this.form = this.emptyForm();
    this.attachments = [];
    this.dialogVisible = true;
  }

  // Called when the dialog fully hides (✕ or Cancel). Reset transient state and clear any
  // suggestion lists so a body-appended dropdown panel can't linger visually after close.
  onDialogHide(): void {
    this.editingId = null;
    this.lockedEdit = false;
    this.optionSuggestions = [];
    this.locationSuggestions = [];
    // Force any open (body-appended) multiselect panel shut so it can't linger on the page
    // after the dialog closes.
    this.multiSelects?.forEach(ms => ms.hide());
  }

  openEdit(outing: Outing): void {
    this.editingId = outing.id;
    // Editing a Done outing = "fix the photos link" only: lock every other field.
    this.lockedEdit = outing.status === OutingStatus.Done;
    // The edit status select doesn't include Done; keep the real status when locked,
    // otherwise show the current status.
    this.form = {
      projectId: outing.projectId,
      pmId: outing.pmId,
      eventName: outing.eventName,
      plannedDate: outing.plannedDate ? new Date(outing.plannedDate) : null,
      status: outing.status,
      location: outing.location ?? '',
      startTime: outing.startTime ?? '',
      budget: outing.budget,
      actualSpend: outing.actualSpend ?? null,
      remarks: outing.remarks ?? '',
      photosFolderUrl: outing.photosFolderUrl ?? '',
      participantIds: outing.participants.map(p => p.employeeId),
      attendedIds: outing.participants.filter(p => p.attended).map(p => p.employeeId)
    };
    this.attachments = [];
    this.loadAttachments(outing.id);
    this.dialogVisible = true;
  }

  // ---------- attachments (photos + bills) ----------
  private loadAttachments(outingId: string): void {
    this.outingService.getAttachments(outingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (list) => this.attachments = list, error: () => {} });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0 || !this.editingId) return;

    this.uploadingPhoto = true;

    // Upload sequentially so the gallery updates as each finishes.
    const uploads = Array.from(files);
    let remaining = uploads.length;
    for (const file of uploads) {
      this.outingService.uploadAttachment(this.editingId, OutingAttachmentKind.Photo, file)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (att) => {
            this.attachments = [...this.attachments, att];
            if (--remaining === 0) this.uploadingPhoto = false;
          },
          error: (err) => {
            this.uploadingPhoto = false;
            this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: err.message });
          }
        });
    }
    input.value = ''; // allow re-selecting the same file
  }

  downloadAttachment(att: OutingAttachment): void {
    this.outingService.downloadAttachment(att.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => saveAs(blob, att.fileName),
        error: (err) => this.messageService.add({ severity: 'error', summary: 'Download failed', detail: err.message })
      });
  }

  deleteAttachment(att: OutingAttachment): void {
    this.confirmationService.confirm({
      message: `Delete "${att.fileName}"?`,
      header: 'Delete file',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.outingService.deleteAttachment(att.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => { this.attachments = this.attachments.filter(a => a.id !== att.id); },
            error: (err) => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: err.message })
          });
      }
    });
  }

  // ---------- attendance popup (mark Done, or correct attendance afterwards) ----------

  // Planning -> Done. Everyone starts ticked: the PM unticks the few who didn't show, which is
  // faster than building the list up and preserves the previous "invited = participated" default.
  openComplete(outing: Outing): void {
    this.completeTarget = outing;
    this.completeMode = 'complete';
    this.attendanceChecks = {};
    for (const p of outing.participants) {
      this.attendanceChecks[p.employeeId] = true;
    }
    this.completeVisible = true;
  }

  // Already Done — reopen to fix attendance (the PM often learns the truth a day or two later).
  // Seeded from what was actually saved, not from RSVP.
  openAttendance(outing: Outing): void {
    this.completeTarget = outing;
    this.completeMode = 'attendance';
    this.attendanceChecks = {};
    for (const p of outing.participants) {
      this.attendanceChecks[p.employeeId] = p.attended;
    }
    this.completeVisible = true;
  }

  get attendanceParticipants(): OutingParticipant[] {
    return [...(this.completeTarget?.participants ?? [])]
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }

  get attendedCheckedCount(): number {
    return this.attendanceParticipants.filter(p => this.attendanceChecks[p.employeeId]).length;
  }

  get allAttendanceChecked(): boolean {
    const list = this.attendanceParticipants;
    return list.length > 0 && list.every(p => this.attendanceChecks[p.employeeId]);
  }

  toggleAllAttendance(): void {
    const next = !this.allAttendanceChecked;
    for (const p of this.attendanceParticipants) {
      this.attendanceChecks[p.employeeId] = next;
    }
  }

  // Shortcut for PMs who trust the RSVP replies — copies them in as a starting point, still editable.
  copyRsvpToAttendance(): void {
    for (const p of this.attendanceParticipants) {
      this.attendanceChecks[p.employeeId] = p.confirmed;
    }
  }

  saveComplete(): void {
    const outing = this.completeTarget;
    if (!outing) return;

    const attendedIds = this.attendanceParticipants
      .filter(p => this.attendanceChecks[p.employeeId])
      .map(p => p.employeeId);

    this.completing = true;
    const dto: UpdateOuting = {
      projectId: outing.projectId,
      pmId: outing.pmId,
      eventName: outing.eventName,
      plannedDate: outing.plannedDate,
      // 'attendance' mode re-sends the current status (Done) so the backend takes no email branch.
      status: this.completeMode === 'complete' ? OutingStatus.Done : outing.status,
      location: outing.location,
      startTime: outing.startTime,
      budget: outing.budget,
      actualSpend: outing.actualSpend,
      remarks: outing.remarks,
      // Unchanged — a differing photo link would trigger a Marketing re-send.
      photosFolderUrl: outing.photosFolderUrl,
      participantIds: outing.participants.map(p => p.employeeId),
      attendedIds
    };

    const completing = this.completeMode === 'complete';

    this.outingService.update(outing.id, dto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.completing = false;
        this.completeVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: completing ? 'Outing completed' : 'Attendance updated',
          detail: completing
            ? `${attendedIds.length} participated. Marketing will be notified with the photos link.`
            : `${attendedIds.length} of ${outing.participants.length} recorded as participated.`
        });
        this.loadOutings();
      },
      error: (err) => {
        this.completing = false;
        this.messageService.add({
          severity: 'error',
          summary: completing ? 'Failed to complete' : 'Failed to update attendance',
          detail: err.message
        });
      }
    });
  }

  // ---------- email Marketing the photos link (Done outings) ----------
  openEmailDialog(outing: Outing): void {
    this.emailTarget = outing;
    this.emailPreview = null;
    this.emailVisible = true;
    this.emailLoadingPreview = true;

    this.outingService.getMarketingEmailPreview(outing.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview) => {
          this.emailPreview = preview;
          this.emailLoadingPreview = false;
        },
        error: (err) => {
          this.emailLoadingPreview = false;
          this.emailVisible = false;
          this.messageService.add({ severity: 'error', summary: 'Could not load email details', detail: err.message });
        }
      });
  }

  sendMarketingEmail(): void {
    const outing = this.emailTarget;
    if (!outing) return;

    this.emailSending = true;
    this.outingService.resendMarketingEmail(outing.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.emailSending = false;
          this.emailVisible = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Email sent',
            detail: `Photos link sent to ${result.recipients.join(', ')}.`
          });
        },
        error: (err) => {
          this.emailSending = false;
          this.messageService.add({ severity: 'error', summary: 'Failed to send', detail: err.message });
        }
      });
  }

  // When the project changes, auto-fill the PM with that project's manager.
  onProjectChange(): void {
    const project = this.projects.find(p => p.id === this.form.projectId);
    if (project) {
      this.form.pmId = project.managerId;
    }
  }

  private validateForm(): string | null {
    if (!this.form.projectId) return 'Please select a project.';
    if (!this.form.eventName?.trim()) return 'Event name is required.';
    if (!this.form.plannedDate) return 'Planned date is required.';
    if (this.form.budget != null && this.form.budget < 0) return 'Budget cannot be negative.';
    // Creating a (Planning) outing emails participants their invitation — make sure there are some.
    if (!this.editingId && this.form.participantIds.length === 0) {
      return 'Add participants — they receive the invitation email when the outing is created.';
    }
    // NOTE: marking Done deliberately has NO photos prerequisite. Photos are uploaded in-app and the
    // Marketing email links to the public photos page, so the old "OneDrive link required" rule was
    // removed from the backend — keeping it here blocked the save on a field the form no longer has.
    return null;
  }

  save(): void {
    const error = this.validateForm();
    if (error) {
      this.messageService.add({ severity: 'warn', summary: 'Check the form', detail: error });
      return;
    }

    // Local date -> ISO date (no timezone shift for a plain calendar date).
    const d = this.form.plannedDate!;
    const plannedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    this.saving = true;

    const base: CreateOuting = {
      projectId: this.form.projectId!,
      pmId: this.form.pmId ?? undefined,
      eventName: this.form.eventName.trim(),
      plannedDate,
      status: this.form.status,
      location: this.form.location?.trim() || undefined,
      startTime: this.form.startTime?.trim() || undefined,
      budget: this.form.budget ?? 0,
      actualSpend: this.form.actualSpend ?? undefined,
      remarks: this.form.remarks?.trim() || undefined,
      photosFolderUrl: this.form.photosFolderUrl?.trim() || undefined,
      participantIds: this.form.participantIds
    };

    const request$ = this.editingId
      ? this.outingService.update(this.editingId, {
          ...base,
          attendedIds: this.form.attendedIds.filter(id => this.form.participantIds.includes(id))
        } as UpdateOuting)
      : this.outingService.create(base);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving = false;
        this.dialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.editingId ? 'Outing updated' : 'Outing created',
          detail: this.saveEmailHint()
        });
        this.loadOutings();
        this.refreshLookups();
      },
      error: (err) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err.message });
      }
    });
  }

  // Toast hint about which automatic email this save triggers.
  private saveEmailHint(): string {
    if (!this.editingId) return 'Participants invited and Marketing notified.';
    if (this.form.status === OutingStatus.Cancelled) return 'Participants and Marketing notified of cancellation.';
    return 'Saved successfully.';
  }

  confirmDelete(outing: Outing): void {
    this.confirmationService.confirm({
      message: `Delete the outing "${outing.eventName}"? This cannot be undone.`,
      header: 'Delete outing',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.outingService.delete(outing.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Outing removed.' });
            this.loadOutings();
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: err.message });
          }
        });
      }
    });
  }

  exportToExcel(): void {
    if (!this.outings.length) {
      this.messageService.add({ severity: 'warn', summary: 'Nothing to export', detail: 'No outings for the current filters.' });
      return;
    }
    this.messageService.add({ severity: 'info', summary: 'Preparing export', detail: 'Generating Excel file…' });
    this.outingService.exportToExcel(this.currentFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          saveAs(blob, 'Team_Outings.xlsx');
          this.messageService.add({ severity: 'success', summary: 'Export ready', detail: 'Your Excel file has been downloaded.' });
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Export failed', detail: err.message });
        }
      });
  }

  // ---------- display helpers ----------
  statusSeverity(status: OutingStatus): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case OutingStatus.Planning: return 'info';
      case OutingStatus.Done: return 'success';
      case OutingStatus.Cancelled: return 'danger';
      default: return 'secondary';
    }
  }

  statusLabel(status: OutingStatus): string {
    return this.statusLabels[status];
  }

  // Participant names for a table row's hover tooltip. While planning, a ✓ marks who replied to
  // their RSVP link (intent). Once done, the ✓ marks who the PM recorded as having actually
  // participated, and absentees are called out — the two are genuinely different sets.
  participantNames(outing: Outing): string {
    if (!outing.participants.length) {
      return 'No participants';
    }
    if (outing.status === OutingStatus.Done) {
      return outing.participants
        .map(p => p.attended ? `${p.employeeName} ✓` : `${p.employeeName} (absent)`)
        .join(', ');
    }
    return outing.participants
      .map(p => p.confirmed ? `${p.employeeName} ✓` : p.employeeName)
      .join(', ');
  }

  // How many participants the PM recorded as having actually participated (Done outings).
  attendedCount(outing: Outing): number {
    return outing.participants.filter(p => p.attended).length;
  }

  // Count of participants who replied via their RSVP link. Planning-only headcount signal —
  // never a participation measure (people forget to click even when they attend).
  confirmedCount(outing: Outing): number {
    return outing.participants.filter(p => p.confirmed).length;
  }

  openPhotos(outing: Outing): void {
    if (outing.photosFolderUrl) {
      window.open(outing.photosFolderUrl, '_blank', 'noopener');
    }
  }

  quarterLabel(outing: Outing): string {
    return `Q${outing.quarter} ${outing.year}`;
  }
}
